import React, { useMemo } from 'react';
import './oneButton-css/TitleInfoModal.css';
import DashboardModal from './DashboardModal';

export type TitleInfoModalProps = {
    open: boolean;
    onClose: () => void;
    /** Raw `locations[0]` (or similar) from Customer Title Info API */
    raw: Record<string, unknown> | null | undefined;
    theme: 'dark' | 'light';
};

function pickStr(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
    if (!obj) return '';
    for (const k of keys) {
        const v = obj[k];
        if (v == null) continue;
        const s = String(v).trim();
        if (s !== '') return s;
    }
    return '';
}

function formatDisplay(value: string): string {
    return value.trim() === '' ? 'N/A' : value;
}

/**
 * Modal showing property title fields (APN, FIPS, unit fields) from Black Knight / title API payload.
 */
export const TitleInfoModal: React.FC<TitleInfoModalProps> = ({ open, onClose, raw, theme }) => {
    const fields = useMemo(() => {
        const o = raw ?? {};
        
        let unitCodeStr = '';
        const useCode = pickStr(o, ['useCode', 'UseCode']);
        const useCodeDesc = pickStr(o, ['useCodeDescription', 'UseCodeDescription', 'unitCode', 'UnitCode']);
        if (useCode && useCodeDesc) {
            unitCodeStr = `${useCode} - ${useCodeDesc}`;
        } else if (useCode) {
            unitCodeStr = useCode;
        } else {
            unitCodeStr = useCodeDesc;
        }

        return {
            owner: pickStr(o, ['owner', 'Owner', 'OwnerName', 'ownerName']),
            apn: pickStr(o, ['apn', 'APN', 'parcelId', 'ParcelId', 'legalDescription']),
            fips: pickStr(o, ['fips', 'FIPS', 'fipsCode']),
            unitNumber: pickStr(o, ['unitNumber', 'UnitNumber', 'unit_number']),
            unitType: pickStr(o, ['unitType', 'UnitType', 'unit_type']),
            unitCode: unitCodeStr,
        };
    }, [raw]);

    const leftCol: { label: string; value: string }[] = [
        { label: 'Owner', value: formatDisplay(fields.owner) },
        { label: 'APN', value: formatDisplay(fields.apn) },
        { label: 'FIPS', value: formatDisplay(fields.fips) },
    ];

    const rightCol: { label: string; value: string }[] = [
        { label: 'Unit Number', value: formatDisplay(fields.unitNumber) },
        { label: 'Unit Type', value: formatDisplay(fields.unitType) },
        { label: 'Unit Code', value: formatDisplay(fields.unitCode) },
    ];

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            width={560}
            zIndex={12050}
            theme={theme}
            getContainer={() => document.body}
            className="obd-title-info-modal"
            rootClassName="obd-title-info-modal-root"
            title="Title Info"
        >
            <div className="obd-title-info-modal__body">
                <div className="obd-title-info-modal__grid">
                    <div className="obd-title-info-modal__col">
                        {leftCol.map((r) => (
                            <div key={r.label} className="obd-title-info-modal__field">
                                <span className="obd-title-info-modal__label">{r.label}</span>
                                <span className="obd-title-info-modal__value">{r.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="obd-title-info-modal__col">
                        {rightCol.map((r) => (
                            <div key={r.label} className="obd-title-info-modal__field">
                                <span className="obd-title-info-modal__label">{r.label}</span>
                                <span className="obd-title-info-modal__value">{r.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DashboardModal>
    );
};

export default TitleInfoModal;
